import React, { useState, useEffect } from "react";
import { Button, Space, Tag, Tooltip, Typography } from "antd";
import { DownloadOutlined, FileOutlined } from "@ant-design/icons";
import { GlobalFileResource } from "../../../types/file/GlobalFileResourceTypes";
import { getGlobalFileResources, downloadGlobalFileResource } from "../../../api/file/globalFileResourcesApi";
import { DataTable, SectionCard } from "../../../components/shared";
import { useNotifications } from "../../../context/NotificationContext";

const { Text } = Typography;

const GlobalFileResourcesManagement: React.FC = () => {
    const { showError } = useNotifications();
    const [resources, setResources] = useState<GlobalFileResource[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);

    useEffect(() => {
        fetchResources();
    }, []);

    const fetchResources = async () => {
        setLoading(true);
        try {
            const data = await getGlobalFileResources();
            setResources(data || []);
        } catch (error) {
            console.error("Error fetching global file resources:", error);
            showError("Failed to load global file resources");
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (resource: GlobalFileResource) => {
        setDownloadingId(resource.id);
        try {
            await downloadGlobalFileResource(resource.id, resource.file_name);
        } catch (error) {
            console.error("Error downloading file resource:", error);
            showError("Failed to download file");
        } finally {
            setDownloadingId(null);
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / 1048576).toFixed(1)} MB`;
    };

    const columns = [
        {
            title: "Name",
            key: "name",
            render: (_: unknown, record: GlobalFileResource) => (
                <div>
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <FileOutlined style={{ color: "#1677ff" }} />
                        <Text strong>{record.name}</Text>
                    </span>
                    <Text type="secondary" style={{ fontSize: 12 }}>{record.file_name}</Text>
                </div>
            ),
        },
        {
            title: "Category",
            dataIndex: "category",
            key: "category",
            width: 140,
            render: (v: string | null) => v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>,
        },
        {
            title: "Description",
            dataIndex: "description",
            key: "description",
            ellipsis: true,
            width: "35%",
            render: (v: string | null) => v || <Text type="secondary">—</Text>,
        },
        {
            title: "Type",
            dataIndex: "file_type",
            key: "file_type",
            width: 130,
            render: (v: string) => <Tag color="blue">{v}</Tag>,
        },
        {
            title: "Size",
            dataIndex: "file_size",
            key: "file_size",
            width: 90,
            render: (v: number) => formatFileSize(v),
        },
        {
            title: "",
            key: "actions",
            width: 100,
            render: (_: unknown, record: GlobalFileResource) => (
                <Space size={2}>
                    <Tooltip title="Download">
                        <Button
                            type="text"
                            size="small"
                            icon={<DownloadOutlined />}
                            loading={downloadingId === record.id}
                            onClick={(e) => { e.stopPropagation(); handleDownload(record); }}
                        >
                            Download
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <SectionCard
            title="Global File Resources"
            subtitle="Files provided by your platform administrator for clinical use"
            noPadding
        >
            <DataTable
                columns={columns}
                dataSource={resources}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
                emptyTitle="No global file resources available"
                emptyDescription="Your administrator has not yet deployed any file resources to this clinic"
                bare
            />
        </SectionCard>
    );
};

export default GlobalFileResourcesManagement;
